import { Component, effect, inject, input } from '@angular/core';
import { PortfolioSeriesSummary } from 'src/app/models/classes/portfolio-series-summary.class';
import { CurrencyFormatPipe } from 'src/app/pipes/currency-format.pipe';
import { CHARCOAL_GREY, GROSVENOR_BLUE, LIGHT_GREY } from '@gcm/gcm-ui-angular';
import { GcmChartComponent } from '../../gcm-chart/gcm-chart.component';
import { BaseWidgetComponent } from '../../base-widget/base-widget.component';
import { DataTableCore } from 'highcharts/highmaps';

@Component({
  selector: 'app-series-commitment-widget',
  imports: [
    BaseWidgetComponent,
    GcmChartComponent,
  ],
  templateUrl: './series-commitment-widget.component.html',
  styleUrl: './series-commitment-widget.component.scss'
})
export class SeriesCommitmentWidgetComponent {
  private currencyFormat = inject(CurrencyFormatPipe);

  readonly series = input.required<PortfolioSeriesSummary>();
  readonly portfolioId = input.required<number>();
  readonly currency = input.required<string>();
  chartConfig!: DataTableCore;
  cumulativeChartStyle = { width: '100%', display: 'flex' };

  private readonly rebuildChart = effect(() => {
    this.series();
    if (this.series()) {
      this.buildWidget();
    }
  });

  buildWidget() {
    const currency = this.series().currency ?? this.currency();
    const seriesData = this.series();
    const currencyFormat = this.currencyFormat;
    this.chartConfig = {
      chart: {
        type: 'column',
        height: 190,
        margin: 0,
        backgroundColor: '#fbfbfb',
        marginRight: 90
      },
      xAxis: {
        visible: false
      },
      yAxis: {
        visible: false
      },
      title: {
        text: ''
      },
      legend: {
        shadow: false,
        enabled: true,
        align: 'right',
        verticalAlign: 'middle',
        layout: 'vertical',
        itemMarginTop: 13,
        y: 10,
        x: 10,
        labelFormatter: function (this: Highcharts.Point | Highcharts.Series) {
          if (this.name === "Total Committed") {
            const valToShow = currencyFormat.transform(Number(seriesData.commitment), currency);
            return `<b><div style="color: ${CHARCOAL_GREY}"><span>` + valToShow + '</span></b><br/><span style="font-weight: 400">' + this.name + '</span></div>';
          }
          if (this.name === "Capital Funded") {
            const valToShow = currencyFormat.transform(Number(seriesData.contributions), currency);
            const percentageToshow = (seriesData.capitalFundedPercent * 100).toFixed(2);
            return `<b><div style="color: ${GROSVENOR_BLUE}"><span>` + valToShow + ' (' + percentageToshow + '%) </b></span><br/><span style="font-weight: 400">' + this.name + '</span></div>';
          }
        },
        events: {
          itemClick: () => false
        }
      },
      tooltip: {
        enabled: false
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: false
          }
        },
        column: {
          grouping: false,
          shadow: false,
          borderWidth: 0,
          states: {
            hover: {
              enabled: false
            },
            inactive: {
              opacity: 1
            }
          },
        }
      },
      series: [{
        name: 'Total Committed',
        color: LIGHT_GREY,
        data: Array(1).fill(seriesData.commitment),
        pointPadding: 0.22,
        pointPlacement: 0.1,
        type: 'column'
      }, {
        name: 'Capital Funded',
        color: GROSVENOR_BLUE,
        data: Array(1).fill(seriesData.contributions),
        pointPadding: 0.28,
        pointPlacement: 0.2,
        type: 'column'
      }],
      responsive: {
        rules: [{
          condition: {
            maxWidth: 425
          },
          chartOptions: {
            series: [{
              name: 'Total Committed',
              color: LIGHT_GREY,
              data: Array(1).fill(seriesData.commitment),
              pointPadding: 0.35,
              pointPlacement: 0.1,
              type: 'column'
            }, {
              name: 'Capital Funded',
              color: GROSVENOR_BLUE,
              data: Array(1).fill(seriesData.contributions),
              pointPadding: 0.4,
              pointPlacement: 0.2,
              type: 'column'
            }],
          }
        }]
      }
    }
  }
}
